import { Bars3Icon, BuildingOfficeIcon, DocumentCheckIcon, RectangleStackIcon, TagIcon, VariableIcon } from '@heroicons/react/20/solid';
import { ReactNode } from 'react';
import { useFirmContext } from '../../providers/FirmProvider';
import { Link } from 'react-router-dom';
import { BadgeV3, TooltipV2 } from '@cimplico/components';
import { CustomWorksheetTemplate } from '../../types/custom-worksheet-v2';

import EditDetailsForm from './EditDetailsForm';
import CWTPrimaryActions from './CWTPrimaryActions';
import CWTStatusBadge from './CWTStatusBadge';
import CWTSecondaryActions from './CWTSecondaryActions';
import plural from '../../utilities/plural';
import { EntityTypeEnum } from '../../types/entity';
import { formatMixedCase } from '../../utilities/formatting';

type CWTDetailsProps = {
    template: CustomWorksheetTemplate;
};

function CWTDetails({ template }: CWTDetailsProps): JSX.Element {
    const { shortId, name: firmName } = useFirmContext();

    return (
        <div className="flex flex-col gap-2">
            <div className="text-gray-500 text-sm flex items-center gap-2">
                <Link to={`/${shortId}/dashboard`} className="hover:underline">
                    {firmName}
                </Link>
                <p> / Templates / </p>
                <Link to={`/${shortId}/templates/calculations`} className="hover:underline">
                    Calculations
                </Link>
            </div>
            <div className="flex w-full flex-wrap items-start justify-between gap-y-2">
                <div>
                    <div className="flex items-center">
                        <h1 className="text-gray-800 text-2xl font-bold"> {template.name}</h1>
                        <EditDetailsForm
                            name={template.name}
                            description={template.description ?? ''}
                            tags={template.tags}
                            id={template.id}
                            entityTypes={template.entityTypes}
                            behaviour={template.behaviour}
                            allowAsSchedule={template.allowAsSchedule}
                        />

                        <div className="ml-2">
                            <CWTStatusBadge status={template.status} />
                        </div>
                    </div>
                    <p className="max-w-[800px] text-gray-600 text-sm">{template.description}</p>
                    <div className="flex gap-2 items-center shrink-0 flex-wrap mt-2">
                        {template.tags.map((t) => {
                            return <BadgeV3 key={t} label={t} color="green" leftIcon={<TagIcon />} />;
                        })}
                        <Statistic icon={<Bars3Icon />} count={1} label="list" />
                        <Statistic icon={<RectangleStackIcon />} count={5} label="section" />
                        <Statistic icon={<VariableIcon />} count={32} label="field" />
                        <Statistic icon={<DocumentCheckIcon />} count={59} label="open workpaper" />
                        <EntityTypes types={template.entityTypes} />
                    </div>
                </div>
                <div className="flex gap-2">
                    <CWTSecondaryActions template={template} />
                    <CWTPrimaryActions template={template} />
                </div>
            </div>
        </div>
    );
}

export default CWTDetails;

type StatisticProps = {
    icon: ReactNode;
    count: number;
    label: string;
};

function Statistic({ icon, count, label }: StatisticProps): JSX.Element {
    return (
        <div className="flex gap-1 items-center text-sm text-gray-500">
            <div className="w-4 h-4">{icon}</div>
            <p className="font-bold text-gray-700">{count}</p>
            <p>{plural(count, label)}</p>
        </div>
    );
}

function EntityTypes({ types }: { types: EntityTypeEnum[] }): JSX.Element {
    const friendlyTypes = types.map((type) => formatMixedCase(type));

    if (types.length === 0) {
        return <></>;
    }

    return (
        <div className="flex gap-1 items-center text-sm text-gray-500">
            <BuildingOfficeIcon className="w-4 h-4" />
            {friendlyTypes[0]}
            {friendlyTypes.length > 1 && (
                <TooltipV2 message={friendlyTypes.slice(1)}>
                    <p className="ml-0.5 select-none ">+{friendlyTypes.slice(1).length}</p>
                </TooltipV2>
            )}
        </div>
    );
}
